import React, { useState } from "react";
import { useIrAPadre } from "@/components/BotonVolver";
import { abrirVisor } from "@/lib/visor";
import { useParams } from "react-router-dom";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import { useData } from "@/context/DataContext";
import { DocAcciones } from "@/components/documentos/DocAcciones";
import { MatterSecciones } from "@/components/MatterSecciones";
import { Sheet, Input } from "@/pages/Familia";
import { formatApiErrorDetail } from "@/lib/api";

const Field = ({ label, value }) =>
  value ? (
    <div className="flex justify-between gap-4 py-2.5 border-b border-[#E5DFD5]/70 last:border-0">
      <span className="text-sm font-semibold text-[#6E7A74]">{label}</span>
      <span className="text-base text-[#1C2421] text-right">{value}</span>
    </div>
  ) : null;

const SectionTitle = ({ icon: Icon, children }) => (
  <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-[#1C2421] mt-7 mb-3">
    <Icon size={20} className="text-[#3A6B56]" /> {children}
  </h2>
);

export default function VehiculoDetail() {
  const { id } = useParams();
  const irA = useIrAPadre();
  const { getVehiculoById, getMattersByVehiculo, getDocumentsForVehiculo, updateVehiculo, deleteVehiculo, getDocumentoBlobUrl, eliminarDocumento, restaurarDocumento } = useData();
  const veh = getVehiculoById(id);
  const [edit, setEdit] = useState(null);
  const [confirmDel, setConfirmDel] = useState(false);
  const [busy, setBusy] = useState(false);
  const [openingDoc, setOpeningDoc] = useState(null);
  const [confirmDelDoc, setConfirmDelDoc] = useState(null);
  const [deletingDoc, setDeletingDoc] = useState(false);

  if (!veh) {
    return (
      <div className="space-y-4">
        <button onClick={() => irA("/vehiculos")} className="inline-flex items-center gap-1.5 text-[#3A6B56] font-semibold"><Icons.ChevronLeft size={22} /> Volver</button>
        <p className="text-base text-[#6E7A74]">No encontramos este vehículo.</p>
      </div>
    );
  }

  const matters = getMattersByVehiculo(veh.id);
  const docs = getDocumentsForVehiculo(veh.id);
  const matterDocIds = new Set(matters.flatMap((m) => m.documents || []));
  const docsSueltos = docs.filter((d) => !matterDocIds.has(d.id));

  const openEdit = () => setEdit({
    alias: veh.alias || "", matricula: veh.matricula || "", marca: veh.marca || "", modelo: veh.modelo || "",
    anio: veh.anio || "", vin: veh.vin || "", titular: veh.titular || "", notas: veh.notas || "", icon: "Car",
  });

  const saveEdit = async () => {
    if (!edit.alias.trim()) return toast.error("El alias no puede estar vacío.");
    setBusy(true);
    try { await updateVehiculo(veh.id, { ...edit, alias: edit.alias.trim() }); toast.success("Vehículo actualizado"); setEdit(null); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo guardar."); }
    finally { setBusy(false); }
  };

  const doDelete = async () => {
    setBusy(true);
    try { await deleteVehiculo(veh.id); toast.success("Vehículo eliminado"); irA("/vehiculos"); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo eliminar."); setBusy(false); }
  };

  const openDoc = async (d) => {
    setOpeningDoc(d.id);
    try { abrirVisor(d); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo abrir el documento."); }
    finally { setOpeningDoc(null); }
  };

  const doDeleteDoc = async () => {
    setDeletingDoc(true);
    const d = confirmDelDoc;
    try {
      await eliminarDocumento(d.id);
      setConfirmDelDoc(null);
      toast.success("Documento eliminado", { description: d.filename, action: { label: "Deshacer", onClick: async () => { try { await restaurarDocumento(d.id); toast.success("Documento restaurado"); } catch (e) { toast.error("No se pudo restaurar."); } } } });
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo eliminar."); }
    finally { setDeletingDoc(false); }
  };

  return (
    <div className="space-y-4" data-testid="vehiculo-detail">
      <button data-testid="vehiculo-detail-back" onClick={() => irA("/vehiculos")} className="inline-flex items-center gap-1.5 text-[#3A6B56] font-semibold text-base min-h-[44px]"><Icons.ChevronLeft size={22} /> Vehículos</button>

      <header className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-12 h-12 rounded-xl bg-[#E8F0EC] flex items-center justify-center shrink-0"><Icons.Car size={26} className="text-[#3A6B56]" /></div>
          <div className="min-w-0">
            <h1 className="font-heading text-2xl font-bold text-[#1C2421] truncate">{veh.alias}</h1>
            <p className="text-base text-[#6E7A74] truncate">{[veh.marca, veh.modelo].filter(Boolean).join(" ") || "Vehículo"}</p>
          </div>
        </div>
        <button data-testid="vehiculo-edit" onClick={openEdit} className="shrink-0 w-11 h-11 rounded-xl bg-[#F2EDE4] flex items-center justify-center hover:bg-[#E5DFD5]"><Icons.Pencil size={18} className="text-[#4A5550]" /></button>
      </header>

      <div className="bg-white border border-[#E5DFD5] rounded-2xl p-5">
        <Field label="Matrícula" value={veh.matricula} />
        <Field label="Marca" value={veh.marca} />
        <Field label="Modelo" value={veh.modelo} />
        <Field label="Año" value={veh.anio} />
        <Field label="VIN / bastidor" value={veh.vin} />
        <Field label="Titular" value={veh.titular} />
        <Field label="Notas" value={veh.notas} />
      </div>

      <SectionTitle icon={Icons.FileText}>Documentos asociados</SectionTitle>
      {docs.length > 0 ? (
        <div className="space-y-2.5">
          {docs.map((d) => {
            const DIcon = d.type === "Imagen" ? Icons.Image : Icons.FileText;
            return (
              <div key={d.id} data-testid={`vehiculo-doc-${d.id}`} className="w-full flex flex-wrap items-center gap-3 bg-white border border-[#E5DFD5] rounded-xl p-3.5 hover:border-[#3A6B56] transition-colors">
                <button onClick={() => openDoc(d)} disabled={openingDoc === d.id} data-testid={`vehiculo-doc-open-${d.id}`} className="flex items-center gap-3 flex-1 min-w-0 text-left disabled:opacity-60">
                  <div className="w-10 h-10 rounded-lg bg-[#E8F0EC] flex items-center justify-center shrink-0"><DIcon size={20} className="text-[#3A6B56]" /></div>
                  <div className="min-w-0 flex-1"><p className="font-semibold text-base text-[#1C2421] truncate">{d.filename}</p><p className="text-sm text-[#6E7A74]">{d.size_label || d.size} · {d.date_label}</p></div>
                  {openingDoc === d.id ? <Icons.Loader2 size={18} className="text-[#4A5550] animate-spin shrink-0" /> : <Icons.Eye size={18} className="text-[#4A5550] shrink-0" />}
                </button>
                <button data-testid={`vehiculo-doc-delete-${d.id}`} onClick={() => setConfirmDelDoc(d)} aria-label="Eliminar documento" className="w-10 h-10 rounded-lg bg-[#FEF2F2] flex items-center justify-center hover:bg-[#FEE2E2] shrink-0"><Icons.Trash2 size={18} className="text-[#B91C1C]" /></button>
                <DocAcciones doc={d} testid={`vehiculo-doc-acc-${d.id}`} />
              </div>
            );
          })}
        </div>
      ) : (
        <p className="text-base text-[#6E7A74] bg-white border border-[#E5DFD5] rounded-xl p-4">Aún no hay documentos en este vehículo.</p>
      )}

      <SectionTitle icon={Icons.Layers}>Documentación, mantenimiento y seguros</SectionTitle>
      <MatterSecciones matters={matters} docs={docsSueltos} scheme="vehiculo" testidPrefix="vehiculo-sec" onOpenDoc={openDoc} emptyText="Todavía no hay documentación ni asuntos en este vehículo." />

      {edit && (
        <Sheet onClose={() => setEdit(null)} testid="vehiculo-edit-form">
          <h2 className="font-heading text-2xl font-bold text-[#1C2421] mb-5">Editar vehículo</h2>
          <Input label="Alias" value={edit.alias} onChange={(v) => setEdit({ ...edit, alias: v })} testid="vehiculo-edit-alias" />
          <Input label="Matrícula" value={edit.matricula} onChange={(v) => setEdit({ ...edit, matricula: v })} testid="vehiculo-edit-matricula" />
          <Input label="Marca" value={edit.marca} onChange={(v) => setEdit({ ...edit, marca: v })} testid="vehiculo-edit-marca" />
          <Input label="Modelo" value={edit.modelo} onChange={(v) => setEdit({ ...edit, modelo: v })} testid="vehiculo-edit-modelo" />
          <Input label="Año" value={edit.anio} onChange={(v) => setEdit({ ...edit, anio: v })} testid="vehiculo-edit-anio" />
          <Input label="VIN / bastidor" value={edit.vin} onChange={(v) => setEdit({ ...edit, vin: v })} testid="vehiculo-edit-vin" />
          <Input label="Titular" value={edit.titular} onChange={(v) => setEdit({ ...edit, titular: v })} testid="vehiculo-edit-titular" />
          <Input label="Notas" value={edit.notas} onChange={(v) => setEdit({ ...edit, notas: v })} testid="vehiculo-edit-notas" />
          <button data-testid="vehiculo-edit-save" onClick={saveEdit} disabled={busy} className="w-full min-h-[56px] rounded-xl bg-[#3A6B56] text-white font-semibold text-lg hover:bg-[#2E5645] disabled:opacity-60 mb-2">{busy ? "Guardando…" : "Guardar cambios"}</button>
          <button data-testid="vehiculo-delete" onClick={() => { setEdit(null); setConfirmDel(true); }} className="w-full min-h-[52px] rounded-xl bg-transparent text-[#B91C1C] font-semibold text-base hover:bg-[#FEF2F2] flex items-center justify-center gap-2"><Icons.Trash2 size={18} /> Eliminar vehículo</button>
        </Sheet>
      )}

      {confirmDel && (
        <Sheet onClose={() => setConfirmDel(false)} testid="vehiculo-confirm-delete">
          <h2 className="font-heading text-xl font-bold text-[#1C2421]">¿Eliminar este vehículo?</h2>
          <p className="text-base text-[#6E7A74] mt-2">Se eliminará «{veh.alias}». Sus documentos y asuntos no se borran: quedarán sin vehículo asignado.</p>
          <div className="flex gap-2.5 mt-5">
            <button onClick={() => setConfirmDel(false)} className="flex-1 min-h-[52px] rounded-xl bg-[#F2EDE4] font-semibold text-base border border-[#E5DFD5]">Cancelar</button>
            <button data-testid="vehiculo-confirm-delete-btn" onClick={doDelete} disabled={busy} className="flex-1 min-h-[52px] rounded-xl bg-[#B91C1C] text-white font-semibold text-base disabled:opacity-60">{busy ? "Eliminando…" : "Eliminar"}</button>
          </div>
        </Sheet>
      )}

      {confirmDelDoc && (
        <Sheet onClose={() => setConfirmDelDoc(null)} testid="vehiculo-doc-confirm-delete">
          <h2 className="font-heading text-xl font-bold text-[#1C2421]">¿Eliminar este documento?</h2>
          <p className="text-base text-[#6E7A74] mt-2">Se eliminará «{confirmDelDoc.filename}». Podrás recuperarlo desde la Papelera durante 30 días.</p>
          <div className="flex gap-2.5 mt-5">
            <button onClick={() => setConfirmDelDoc(null)} className="flex-1 min-h-[52px] rounded-xl bg-[#F2EDE4] font-semibold text-base border border-[#E5DFD5]">Cancelar</button>
            <button data-testid="vehiculo-doc-confirm-delete-btn" onClick={doDeleteDoc} disabled={deletingDoc} className="flex-1 min-h-[52px] rounded-xl bg-[#B91C1C] text-white font-semibold text-base disabled:opacity-60">{deletingDoc ? "Eliminando…" : "Eliminar"}</button>
          </div>
        </Sheet>
      )}
    </div>
  );
}
